import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, User } from "lucide-react";
import logo from "@/assets/logo.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Berhasil masuk");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-[#DDF1F7] px-6 py-12"
      data-testid="login-page"
    >
      <div className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-[#2AA8C2]/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-20 h-[28rem] w-[28rem] rounded-full bg-white/70 blur-3xl" />

      <div className="relative w-full max-w-sm fade-up">
        <div className="rounded-2xl border border-white/70 bg-white/80 p-8 shadow-[0_20px_60px_-20px_rgba(42,168,194,0.45)] backdrop-blur-xl">
          <div className="mb-8 flex flex-col items-center text-center">
            <img src={logo} alt="Pengepul ABG" className="h-20 w-20 object-contain" data-testid="login-logo" />
            <h1 className="mt-3 font-heading text-2xl font-bold tracking-tight text-[#14606F]" data-testid="login-app-name">
              Pengepul ABG
            </h1>
            <p className="mt-1 text-sm text-slate-500">Masuk ke akun Anda</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-700">Username</Label>
              <div className="relative">
                <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="username"
                  data-testid="login-username-input"
                  className="rounded-lg border-slate-200 bg-white pl-9 text-slate-900 focus-visible:ring-[#2AA8C2]"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="username"
                  autoComplete="username"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  className="rounded-lg border-slate-200 bg-white pl-9 text-slate-900 focus-visible:ring-[#2AA8C2]"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full rounded-lg bg-[#2AA8C2] text-white transition-colors hover:bg-[#1F8FA6]"
              disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Masuk
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
